import {
  collection,
  CollectionReference,
  orderBy,
  query,
} from 'firebase/firestore';
import { collectionData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { deidentifyAthlete } from '../../utils/deidentifyAthlete';
import { mergeRepetitionMetrics } from '../../utils/testMetrics';
import {
  IAthlete,
  IEntityMap,
  IGroup,
  IMappable,
  ITag,
  ITeam,
  ITestRepetition,
  ITestRepetitionEntity,
  ITestRepetitionIntermediate,
  ITestType,
  IUser,
} from '../types';
import { entitiesProvider, IEntities } from './entitiesProvider';
import { tagsProvider } from './tagsProvider';
import { testTypesProvider } from './testTypesProvider';
import { authenticatedUserProvider } from './userProvider';
import { entitiesAsMap } from './utils/entitiesAsMap';

const repetitions = (
  testId: string,
  deidentify: boolean,
  includeInactiveMetrics: boolean,
  user: IUser
): Observable<ITestRepetition[]> => {
  return collectionData(
    query(
      collection(
        user.services.firestore,
        `orgs/${user.orgId}/tests/${testId}/test_repetitions`
      ) as CollectionReference<ITestRepetitionEntity>,
      orderBy('start')
    ),
    { idField: 'id' }
  ).pipe(
    switchMap(
      (
        repetitionEntities: ITestRepetitionEntity[]
      ): Observable<ITestRepetition[]> =>
        mergeTests(repetitionEntities, deidentify, includeInactiveMetrics)
    )
  );
};

export const repetitionsProvider = (
  testId: string,
  deidentify: boolean = false,
  includeInactiveMetrics: boolean = false
): Observable<ITestRepetition[]> => {
  return authenticatedUserProvider().pipe(
    switchMap(
      repetitions.bind(null, testId, deidentify, includeInactiveMetrics)
    )
  );
};

/**
 * @description merges athlete, tags, and test type with a collection of repetitions from the database
 */
const mergeTests = (
  tests: ITestRepetitionEntity[],
  deidentify: boolean,
  includeInactives: boolean
): Observable<ITestRepetition[]> => {
  const entities$ = entitiesProvider().pipe(
    map((entities: IEntities): [IEntityMap, IEntityMap, IEntityMap] => [
      entitiesAsMap(entities.athletes as IMappable[]),
      entitiesAsMap(entities.groups as IMappable[]),
      entitiesAsMap(entities.teams as IMappable[]),
    ])
  );

  return combineLatest([tagsProvider(), testTypesProvider(), entities$]).pipe(
    map(
      (
        result: [ITag[], ITestType[], [IEntityMap, IEntityMap, IEntityMap]]
      ): ITestRepetitionIntermediate[] => {
        const [tags, testTypes, entities] = result;

        const [athletes, groups, teams] = entities;

        return tests
          .map((test: ITestRepetitionEntity): ITestRepetitionIntermediate => {
            const athlete = athletes[test.athleteId] as IAthlete;
            const newTest: ITestRepetitionIntermediate = {
              ...test,
              testType: testTypes.find(
                (type: ITestType): boolean => type.id === test.testType
              ) as ITestType,
              tags: tags.filter((tag: ITag): boolean =>
                test.tags.includes(tag.id)
              ),
              groups: test.groups.map(
                (id: string): IGroup => groups[id] as IGroup
              ),
              teams: test.groups.map((id: string): ITeam => teams[id] as ITeam),
              athlete:
                deidentify === true && athlete
                  ? deidentifyAthlete(athlete)
                  : athlete,
            };
            return newTest;
          })
          .filter(
            (mTest: ITestRepetitionIntermediate): boolean => !!mTest.athlete
          );
      }
    ),
    switchMap(
      (
        intermediates: ITestRepetitionIntermediate[]
      ): Observable<ITestRepetition[]> => {
        return mergeRepetitionMetrics(intermediates, includeInactives);
      }
    )
  );
};
